import React from 'react';

import styled from '@mui/styles/styled';
import { DistributiveOmit } from '@mui/types';

import Table from '@mui/material/Table';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableBody from '@mui/material/TableBody';
import TableRow, { TableRowProps } from '@mui/material/TableRow';
import styledComp from 'styled-components';
import { FilterList as FilterListIcon } from '@mui/icons-material';
import { Chip, TableSortLabel } from '@mui/material';
import { Styling, Animations, Colors } from '../../../constants';
import { ImagePlaceholder, TagArrowIcon, VaultButton } from '../../Shared';
import vaultImageProxy from '../../Shared/vaultImageProxy';
import { getRotation } from '../../../utils/photoRotation';

const thumbnailWidth = 105;
const thumbnailHeight = 60;

export const ResultsTableContainer = styledComp.div`
  height: 100%;
  overflow-x: auto;
`;

export const ResultsTable = styled(Table)({
  display: 'flex',
  height: '100%',
  'min-width': '1354px',
  'flex-direction': 'column',
  'flex-grow': 1,
});

export const ResultsTableHead = styledComp(TableHead)`
  tr:not(#filterRow) {
    padding-top: 12px;
    height: 30px;
  }
  tr#filterRow {
    height: 55px;
  }
`;

export const ResultsTableBody = styled(TableBody)({
  height: '1px',
  display: 'block',
  'flex-grow': 1,
  overflowY: 'auto',
});

export const ResultsRowStyle = styled(TableRow)({
  display: 'flex',
  'flex-grow': 1,
  'align-items': 'center',
});

interface IResultsTableRowProps {
  $isFocused: boolean;
  $isSelected: boolean;
  children?: React.ReactNode;
}

const rowBorderFromState = (props: IResultsTableRowProps, hover: boolean = false): string => {
  const {
    $isFocused,
    $isSelected,
  } = props;
  if ($isSelected) {
    return Colors.PRIMARY_50;
  }
  if ($isFocused || hover) {
    return Colors.DARK_GREY;
  }
  return Colors.BLACK_20;
};

const rowBackgroundFromState = (props: IResultsTableRowProps, hover: boolean = false): string => {
  const {
    $isFocused,
    $isSelected,
  } = props;
  if ($isSelected) {
    return (hover || $isFocused) ? Colors.PRIMARY_5 : Colors.PRIMARY_3;
  }
  if ($isFocused || hover) {
    return Colors.SELECTED_ITEM;
  }
  return 'white';
};

export const ResultsTableRow = styledComp(ResultsRowStyle) <IResultsTableRowProps>`
  border-top: 1px solid ${(props) => rowBorderFromState(props)};
  border-bottom: 1px solid ${(props) => rowBorderFromState(props)};
  background-color: ${(props) => rowBackgroundFromState(props)};
  transition: border ${Animations.DEFAULT_TRANSITION}, background-color ${Animations.DEFAULT_TRANSITION};
  &:hover {
    background-color: ${(props) => rowBackgroundFromState(props, true)};
    border-top: 1px solid ${(props) => rowBorderFromState(props, true)};
    border-bottom: 1px solid ${(props) => rowBorderFromState(props, true)};
    cursor: pointer;
  }
`;

export const ResultsTableHeaderRow = styled(ResultsRowStyle)({
  'font-weight': '700',
  height: '52px',
  'background-color': '#f5f5f5',
  'padding-right': '16px',
});

const BaseTableCell = styled(TableCell)({
  'font-size': '12px !important',
  flex: 1,
  border: 'none',
  padding: 6,
});

export const ResultsCheckboxCell = styled(BaseTableCell)({
  'max-width': '56px',
});

export const ResultsThumbnailCell = styled(BaseTableCell)({
  'min-width': `${thumbnailWidth + Styling.DEFAULT_SPACING}px`,
  'justify-content': 'center',
});

export const ResultsRowTitleCell = styled(BaseTableCell)({
  'min-width': '240px',
  'overflow-wrap': 'break-word',
  'font-weight': 'bold !important',
});

export const ResultsRowTitleExpandedCell = styled(BaseTableCell)({
  'min-width': '350px',
  'overflow-wrap': 'break-word',
  'font-weight': 'bold !important',
  'font-size': '14px !important',
});

export const StyledTableSortLabel = styledComp(TableSortLabel)`
  &[id="SearchResults.title.sort"] {
    width: 100%;
  }
  &[id="SearchResults.orgTagIds.sort"] {
    width: 130px;
  }
`;

export const ResultHeaderTitleCell = styled(ResultsRowTitleCell)({

});

export const ResultHeaderTitleExpandedCell = styled(ResultsRowTitleExpandedCell)({
  'font-size': '12px !important',
});

export const ResultsDateCell = styled(BaseTableCell)({
  'max-width': '10%',
});

export const ResultsRowCell = styled(BaseTableCell)({
  'min-width': '68px',
  'overflow-wrap': 'break-word',
});

export const ResultsRowFixedCell = styled(ResultsRowCell)({
  position: 'relative',
  left: `${Styling.DEFAULT_SPACING_DOUBLED}px`,
});

export const ResultsTeamCell = styled(BaseTableCell)({
  'max-width': '10%',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
});

export const ResultsHeaderCell = styled(ResultsRowCell)({
  'margin-top': '1px',
  padding: 6,
  'font-weight': 'bold',
  minWidth: `${thumbnailWidth + Styling.DEFAULT_SPACING}px`,
});

// Note: This was changed from th/div to Fragment because table rows can only contain td/th
export const ResultsHeaderCellGroup = React.Fragment;

export const ResultsHeaderTagCell = styled(ResultsHeaderCell)({
  'min-width': '103px',
});

export const ResultHeaderThumbnailCell = styled(ResultsThumbnailCell)({
  fontWeight: 'bold',
  minWidth: `${thumbnailWidth + Styling.DEFAULT_SPACING}px`,
});

export const AssetTagsContainer = styledComp.div`
`;

export const AssetTagsList = styledComp.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px;
`;

export const TagChip = styledComp(Chip)`
  &.MuiChip-root {
    font-size: 9px;
    font-weight: normal;
    height: 16px;
    padding: 0px;
    border-radius: 8px;
    background-color: rgb(235, 235, 235);
  }
  .MuiChip-label {
    padding-left: 4px;
    padding-right: 4px;
    margin: 2px;
    color: rgb(107, 107, 107);
  }
  .MuiChip-deleteIconSmall {
    width: 12px;
    height: 12px;
  }
  .MuiChip-deleteIcon {
    color: rgb(107, 107, 107);
  }
`;

export const TagSpan = styledComp.span`
  font-weight: normal !important;
  font-size: 11px !important;
`;

const ThumbnailWrapper = styledComp.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: ${thumbnailHeight}px;
  width: ${thumbnailWidth}px;
  overflow: hidden;
`;

const thumbnailBaseStyle = `
  display: flex; 
  margin: 0;
  padding: 0;
`;

export const ExchangeThumbnailContainer = styledComp.div`
  display: flex;
`;

export const ExchangeThumbnailImage = styledComp.img`
  width: ${thumbnailWidth / 2}px;
  height: ${thumbnailHeight}px;
  object-fit: contain;
  display: flex; 
  margin: 0;
  padding: 0;
`;

export const ThumbnailImage = (url: string, photoRotation?: number) => {
  const computedRotation = getRotation(photoRotation);
  if (computedRotation) {
    const Image = vaultImageProxy(
      styledComp.img`
        ${thumbnailBaseStyle}
        ${`transform: rotate(${computedRotation.rotation}deg)`};
        ${computedRotation.imageFlipped ? `width:${thumbnailHeight}px;` : 'height: 100%;'};
      `,
    );
    return <ThumbnailWrapper><Image src={url} alt="" /></ThumbnailWrapper>;
  }
  const Image = vaultImageProxy(
    styledComp.img`
        max-width: 100%;
        max-height: 100%;
        ${thumbnailBaseStyle}
      `,
  );
  return <ThumbnailWrapper><Image src={url} alt="" /></ThumbnailWrapper>;
};

export const PlaceholderImage = styledComp(ImagePlaceholder)`
  width: ${thumbnailWidth}px;
  height: ${thumbnailHeight}px;
  object-fit: fill;
  display: flex; 
  margin: 0;
  padding: 0;
  opacity: 0.2;
`;

export const IconCard = styledComp.i`
  width: 25px;
  height: 25px;
  position: absolute;
  bottom: 0;
  left: 80px;
  background-color: white;
`;

export const ThumbnailDiv = styledComp.div` 
  position: relative;
`;

export const InfoContainer = styledComp.div`
  display: flex;
  flex-grow: 1;
  align-items: center;
  justify-content: center;
`;

export const NoItemsText = styledComp.span`
  font-size: 28px;
  color: ${Colors.BLACK_50};
  padding: ${Styling.DEFAULT_SPACING}px;
`;

export const FilterIcon = styled(FilterListIcon)({
  marginBottom: '-7px',
  marginLeft: '-22px',
  color: `${Colors.BLACK_90}`,
  position: 'absolute',
});

export const AddTagButton = styledComp(VaultButton).attrs((props) => ({
  variant: 'tagButton',
}))`
`;
